<template>
    <div id="mapEditor" v-if="currentPosition">
        <div style="display:flex" class="buttons editor" :class="{ fullscreen: fullscreenEnabled }">
            <v-btn dark fab small color="lime" @click="onMarker" v-if="mapMarkerEnabled">
                <v-icon>mdi-map-marker</v-icon>
            </v-btn>

            <v-btn dark fab small color="lime" @click="onCircle" v-if="mapCircleEnabled">
                <v-icon>mdi-shape-circle-plus</v-icon>
            </v-btn>

            <v-btn dark fab small color="lime" @click="onPolygon" v-if="mapPolygonEnabled">
                <v-icon>mdi-shape-polygon-plus</v-icon>
            </v-btn>

            <v-btn dark fab small color="red" @click="onDelete" v-if="shapeProxy&&shapeProxy.id">
                <v-icon>mdi-delete</v-icon>
            </v-btn>
        </div>

        <GmapMap :style="{width: '100%', height: fullscreenEnabled ? '97vh' : '50vh'}"
                 :center="{lat:currentPosition.lat, lng:currentPosition.lng}"
                 :map-type-id="mapType"
                 :options="mapOptions"
                 :zoom="mapZoomLevel"
                 @center_changed="debounceChange('center', $event)"
                 @click="onMap">

            <gmap-marker :position="shapeProxy.position"
                         :draggable="shapeProxy.draggable"
                         :editable="shapeProxy.editable"
                         @position_changed="debounceChange('position', $event)"
                         v-if="shapeProxy && shapeProxy.type === 'marker'"></gmap-marker>

            <gmap-circle :center="shapeProxy.position"
                         :radius="shapeProxy.radius"
                         :draggable="shapeProxy.draggable"
                         :editable="shapeProxy.editable"
                         :options="{strokeColor: (plant ? plant.color : 'black'), fillColor: (plant ? plant.color : 'black')}"
                         @radius_changed="debounceChange('radius', $event)"
                         @drag="debounceChange('position', $event)"
                         v-if="shapeProxy && shapeProxy.type === 'circle'"></gmap-circle>

            <gmap-polygon :paths="shapeProxy.paths"
                          :draggable="shapeProxy.draggable"
                          :editable="shapeProxy.editable"
                          :options="{strokeColor: (plant ? plant.color : 'black'), fillColor: (plant ? plant.color : 'black')}"
                          @paths_changed="debounceChange('paths', $event)"
                          v-if="shapeProxy && shapeProxy.type === 'polygon'"></gmap-polygon>
        </GmapMap>

        <div style="display:flex" class="buttons map" :class="{ fullscreen: fullscreenEnabled }">
            <v-btn dark fab small color="grey" @click="onZoomLevelChange(mapZoomLevel++)" :disabled="mapZoomLevel >= 21">
                <v-icon>mdi-magnify-plus</v-icon>
            </v-btn>

            <v-btn dark fab small color="grey" @click="onZoomLevelChange(mapZoomLevel--)" :disabled="mapZoomLevel <= 0">
                <v-icon>mdi-magnify-minus</v-icon>
            </v-btn>

            <v-btn dark fab small color="grey" @click="onType">
                <v-icon>mdi-terrain</v-icon>
            </v-btn>

            <v-btn dark fab small color="grey" @click="onFullscreenChange">
                <v-icon v-if="fullscreenEnabled">mdi-fullscreen-exit</v-icon>
                <v-icon v-else>mdi-fullscreen</v-icon>
            </v-btn>
        </div>

    </div>
</template>

<script>
    import _ from 'lodash'
    import {Shapes} from '@/components/_helpers/shapes'

    const ZOOM_LEVEL_DEFAULT = 17
    const MAP_TYPE_HYBRID = 'hybrid'
    const MAP_TYPE_ROADMAP = 'roadmap'

    export default {
        name: 'MapEditor',
        props: ['shape', 'plantId'],
        computed: {
            // Mutable prop proxy
            shapeProxy: {
                get() {
                    return (this.shapeObj) ? this.shapeObj : this.shape
                },
                set(newShape) {
                    this.shapeObj = newShape
                }
            },
            plant() {
                return this.$store.getters.plantList.find(plant => plant.uuid === this.plantId)
            },
            mapMarkerEnabled() {
                let plant = this.$store.getters.plantList.find(plant => plant.uuid === this.plantId)
                return (plant && plant.mapMarker !== undefined) ? plant.mapMarker : false
            },
            mapCircleEnabled() {
                let plant = this.$store.getters.plantList.find(plant => plant.uuid === this.plantId)
                return (plant && plant.mapCircle !== undefined) ? plant.mapCircle : false
            },
            mapPolygonEnabled() {
                let plant = this.$store.getters.plantList.find(plant => plant.uuid === this.plantId)
                return (plant && plant.mapPolygon !== undefined) ? plant.mapPolygon : false
            },
        },
        data: () => ({
            // Prop proxys
            shapeObj: null, // prop proxy

            // Map data
            currentPosition: null,
            mapType: MAP_TYPE_HYBRID,
            mapOptions: {
                zoomControl: false,
                mapTypeControl: false,
                scaleControl: false,
                streetViewControl: false,
                rotateControl: false,
                fullscreenControl: false,
                disableDefaultUi: false
            },
            mapZoomLevel: ZOOM_LEVEL_DEFAULT,

            // Control data
            fullscreenEnabled: false,
            markerEnabled: false, // per default it is enabled that someone can set a marker
            circleEnabled: false,
            polygonEnabled: false

        }),
        methods: {
            // Debounce event changes
            debounceChange: _.debounce(function (prop, event) {
                this.onChange(prop, event)
            }, 300),
            onChange(prop, value) {
                // Propagate changes to parent
                if ('position' === prop) {
                    // Process position data depending on shape type. Either
                    // use raw event data (circle) or default position object
                    // (marker)
                    if ('circle' === this.shapeProxy.type) {
                        this.shapeProxy[prop] = {lat: value.latLng.lat(), lng: value.latLng.lng()}
                    } else {
                        this.shapeProxy[prop] = value
                    }
                    this.emitShapeChange()
                } else if ('radius' === prop) {
                    // Update value
                    this.shapeProxy[prop] = value
                    this.emitShapeChange()
                } else if ('paths' === prop) {
                    // Example given at https://github.com/xkjyeah/vue-google-maps/blob/vue2/examples/polygon-editing.html

                    // Reset paths
                    this.shapeProxy.paths = []

                    for (let i=0; i<value.getLength(); i++) {
                        for (let j = 0; j < value.getAt(i).getLength(); j++) {
                            let point = value.getAt(i).getAt(j)

                            // Store single path positions
                            this.shapeProxy.paths.push({lat: point.lat(), lng: point.lng()})
                        }
                    }
                    this.emitShapeChange()
                } else {
                    this.log(`Ignored: ${prop}/${value}`)
                }
            },
            onMap(mouseArgs) {
                this.log(`On map click: ${JSON.stringify(mouseArgs)}`)

                if (!mouseArgs || !mouseArgs.latLng) {
                    return
                }

                let lat = mouseArgs.latLng.lat()
                let lng = mouseArgs.latLng.lng()

                // Create shape object on map
                if (this.markerEnabled) {
                    this.shapeProxy = this.createMarker(lat, lng)
                } else if (this.circleEnabled) {
                    this.shapeProxy = this.createCircle(lat, lng)
                    this.emitShapeChange()
                } else if (this.polygonEnabled) {
                    // When an polygon instance already exist, add path item,
                    // otherwise create new one
                    if (this.shapeProxy && this.shapeProxy.type === 'polygon') {
                        this.shapeProxy.paths.push({lat: lat, lng: lng})
                        this.emitShapeChange()
                    } else {
                        this.shapeProxy = this.createPolygon(lat, lng)
                    }
                }
            },
            onZoomLevelChange(newZoomLevel) {
                if (this.shapeProxy && this.shapeProxy.zoomLevel) {
                    // Set zoom-level
                    this.shapeProxy.zoomLevel = newZoomLevel
                    // Notify change to parent component
                    this.emitShapeChange()
                }
            },
            onType() {
                this.mapType = (this.mapType === MAP_TYPE_HYBRID) ? MAP_TYPE_ROADMAP : MAP_TYPE_HYBRID
            },
            onFullscreenChange() {
                this.fullscreenEnabled = !this.fullscreenEnabled

                let mapEditor = document.getElementById("mapEditor")
                mapEditor.scrollIntoView({behavior: "smooth"})
            },
            onMarker() {
                this.log(`On marker click`)

                // Update buttons' state
                this.toggleButton(true, false, false)

                // Must be saved to proxy object for emitting change to parent.
                // Circle and polygon works with differently and change is
                // automatically emitted.
                this.shapeProxy = this.createMarker(this.currentPosition.lat, this.currentPosition.lng)

                // Emit change
                this.emitShapeChange()
            },
            onCircle() {
                this.log(`On circle click`)

                // Update buttons' state
                this.toggleButton(false, true, false)

                this.createCircle(this.currentPosition.lat, this.currentPosition.lng)
            },
            onPolygon() {
                this.log(`On polygon click`)

                // Update buttons' state
                this.toggleButton(false, false, true)

                this.createPolygon()
            },
            onDelete() {
                this.log(`delete circle or poly`)
                this.shapeProxy = {}
                this.emitShapeChange()
            },
            emitShapeChange() {
                // Notify change -> work-a-round to create a pure JavaScript object
                this.$emit('shape-change', JSON.parse(JSON.stringify(this.shapeProxy)))
            },
            getCurrentPosition() {
                // When position is specified at shape, return it;
                // otherwise use user's current position
                return (this.shapeProxy && this.shapeProxy.position)
                    ? this.shapeProxy.position
                    : this.$store.getters.userPosition
            },
            updateZoomLevel(shape) {
                this.mapZoomLevel = (shape && shape.zoomLevel) ? shape.zoomLevel : ZOOM_LEVEL_DEFAULT
            },
            toggleButton(markerEnabled, circleEnabled, polygonEnabled) {
                this.markerEnabled = markerEnabled
                this.circleEnabled = circleEnabled
                this.polygonEnabled = polygonEnabled
            },
            createMarker(lat, lng) {
                // See definition in vue-google-maps/src/components/marker.js
                let marker = {
                    id: Shapes.MARKER.id,
                    type: Shapes.MARKER.value,
                    position: {
                        lat: lat,
                        lng: lng
                    },
                    draggable: true,
                    editable: true,
                    zoomLevel: this.mapZoomLevel
                }
                return marker
            },
            createCircle(lat, lng) {
                // See definition in vue-google-maps/src/components/circle.js
                let circle = {
                    id: Shapes.CIRCLE.id,
                    type: Shapes.CIRCLE.value,
                    position: {
                        lat: lat,
                        lng: lng
                    },
                    radius: (this.mapZoomLevel <= ZOOM_LEVEL_DEFAULT) ? 15 : 5,
                    draggable: true,
                    editable: true,
                    zoomLevel: this.mapZoomLevel
                }
                return circle
            },
            createPolygon(lat, lng) {
                // See definition in vue-google-maps/src/components/polygon.js
                let polygon = {
                    id: Shapes.POLYGON.id,
                    type: Shapes.POLYGON.value,
                    paths: [{lat: lat, lng: lng}],
                    draggable: true,
                    editable: true,
                    zoomLevel: this.mapZoomLevel
                }
                return polygon
            },
            log(text) {
                console.log(`[MapEditor] ${text}`)
            }
        },
        mounted() {
            this.currentPosition = this.getCurrentPosition()

            // Initial set zoom-level, afterwards through watch
            this.updateZoomLevel(this.shapeProxy)
        }

    };
</script>

<style scoped>

div.buttons.editor {
    padding: 5px 0px;
}
div.buttons.editor.fullscreen {
    /* empty */
}

div.buttons.map {
    padding: 5px 0px;
}

div.buttons.map.fullscreen {
    /* empty */
}

button {
    margin: 2px;
}

</style>
